import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { environmentOm } from 'src/environment-om';

import { ReportsApi } from '../../../api/reports-api';
import { ReportHelper } from '../../report-helper';

@Component({
    selector: 'om-legislative-asset-draft-tip-report',
    templateUrl: './legislativeAssetDraftTipReport.html',
    styleUrls: ['../../reports.css', './legislativeAssetDraftTipReport.css']
})

export class LegislativeAssetDraftTipReportComponent implements OnInit {
    reportParams;
    reportData;
    isGenerating = false;
    error = false;
    errorMessage;
    houseDistrictNumber;
    senateDistrictNumber;
    congressDistrictNumber;
    searchLocation = <any>{};
    searchLocationType = '';

    @ViewChild('mcMap', { static: false }) mapRef: ElementRef<any>;
    defaultBasemap = {
        tileUrl: environmentOm.basemapURL + '/rest/services/basemaps/penndotbasemap/MapServer/tile',
        thumbnailTile: '/7/48/36',
        tileTemplate: '/{level}/{row}/{col}',
        title: 'PennDOT'
    };

    constructor(private readonly reportHelper: ReportHelper,
        private readonly reportsApi: ReportsApi,
        private readonly route: ActivatedRoute,
        titleService: Title) {
        titleService.setTitle(titleService.getTitle() + ' : Legislative Asset Draft TIP Report');
    }

    ngOnInit() {
        this.isGenerating = true;
        this.route.paramMap.subscribe(async (x) => {
            const reportParams = JSON.parse(window.localStorage.getItem(x.get('id')));
            this.houseDistrictNumber = reportParams.houseDistrictNumber ? reportParams.houseDistrictNumber : null;
            this.senateDistrictNumber = reportParams.senateDistrictNumber ? reportParams.senateDistrictNumber : null;
            this.congressDistrictNumber = reportParams.congressDistrictNumber ? reportParams.congressDistrictNumber : null;
            this.reportParams = this.getReportParams();
            await this.loadReport();
            this.loadMap();
            this.getSearchLocation();
            this.isGenerating = false;
        });
    }

    private getReportParams() {
        if (this.congressDistrictNumber) {
            return {
                legisNum: this.congressDistrictNumber.toString(),
                legisType: 'C',
                regionType: 'congress',
                tipType: 'P'
            };
        } else if (this.houseDistrictNumber) {
            return {
                legisNum: this.houseDistrictNumber.toString(),
                legisType: 'H',
                regionType: 'house',
                tipType: 'P'
            };
        } else if (this.senateDistrictNumber) {
            return {
                legisNum: this.senateDistrictNumber.toString(),
                legisType: 'S',
                regionType: 'senate',
                tipType: 'P'
            };
        } else {
            return null;
        }
    }

    private async loadReport() {
        this.reportData = await this.reportsApi.getLegislativeAssetDraftTipReportData(this.reportParams);
    }

    private async loadMap() {
        const params = this.reportParams;
        const projectIds = this.getProjectIds();
        // create search criteria object for applySearchCriteriaToLayer function
        const searchCriteria = {
            region: <any>{}
        };

        switch (params.regionType) {
            case 'congress':
                params.regionType = 'congress_districts';
                searchCriteria.region.congressDistrictNumber = params.legisNum;
                break;
            case 'house':
                params.regionType = 'house_districts';
                searchCriteria.region.houseDistrictNumber = params.legisNum;
                break;
            case 'senate':
                params.regionType = 'senate_districts';
                searchCriteria.region.senateDistrictNumber = params.legisNum;
                break;
            default:
                break;
        }

        const layerId = environmentOm.layers.filter(x => x.name === 'mpmsProjectsAll')[0].id;
        const legislativeLayerId = environmentOm.layers.filter(x => x.name === String(this.reportParams.regionType.split('_')[0]) + 'District')[0].id;
        const symbols = this.reportHelper.getBoundaryLayerProperties(this.mapRef.nativeElement.LayerService.legendService);
        try {
            await this.mapRef.nativeElement.ready;
            await this.mapRef.nativeElement.setBasemapsFromConfig(this.defaultBasemap);
            const layerSettings = [{
                id: layerId,
                query: {
                    operator: 'AND', subqueries: [
                        { field: params.regionType, operator: '=', value: params.legisNum },
                        { field: 'project_id', operator: 'INCLUDES', value: projectIds.toString() },
                        { field: 'tip_proposed_flag', operator: '=', value: 'Y' }
                    ]
                }
            },
            {
                id: legislativeLayerId,
                query: { field: 'leg_district_no', operator: '=', value: parseInt(params.legisNum, 10) },
                opacity: 0.5,
                symbols,
                zoomToLayer: true,
                generateNewLayer: true
                }];
            this.mapRef.nativeElement.LayerService.addMapcoreLayersToMap([...layerSettings].reverse());
        } catch (error) {
        }
    }

    getProjectIds() {
        const projectIds = [];
        for (const project of this.reportData.projects) {
            projectIds.push(project.projectId);
        }
        return projectIds;
    }

    /**
     * Search location object for top of report
     */
    private async getSearchLocation() {
        if (this.congressDistrictNumber) {
            this.searchLocation = await this.reportHelper.getCongressionalDistrictByName(this.congressDistrictNumber);
            this.searchLocationType = this.reportParams.legisType;
        }
        if (this.senateDistrictNumber) {
            this.searchLocation = await this.reportHelper.getSenateDistrictByName(this.senateDistrictNumber);
            this.searchLocationType = this.reportParams.legisType;
        }
        if (this.houseDistrictNumber) {
            this.searchLocation = await this.reportHelper.getHouseDistrictByName(this.houseDistrictNumber);
            this.searchLocationType = this.reportParams.legisType;
        }
    }
}
